import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { RoleBriefing } from "@/components/RoleBriefing";
import { ClaimSources } from "@/components/ClaimSources";
import GeoSpecialty from "@/pages/GeoSpecialty";
import {
  Zap, AlertTriangle, CheckCircle2, GitCompareArrows, FlaskConical, Radar,
  Crosshair, TrendingUp, Globe2, LineChart, Target, ShieldCheck, CalendarDays, Layers,
} from "lucide-react";

const Section = ({ icon: Icon, title, tone = "teal", children, testid }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm shadow-slate-100" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`w-4 h-4 text-${tone}-600`} />
      <h3 className="font-heading text-sm font-bold text-slate-900">{title}</h3>
    </div>
    {children}
  </div>
);

const Li = ({ items, dot = "bg-slate-400" }) => (
  <ul className="space-y-2">
    {items.map((it) => (
      <li key={it.text} className="flex items-start gap-2 text-xs text-slate-700 leading-snug">
        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${dot}`} />
        <span>
          {it.text}
          {(it.ids || it.ref) && <span className="font-mono text-[10px] text-slate-400 ml-1.5">[{(it.ids || [it.ref]).join(", ")}]</span>}
        </span>
      </li>
    ))}
  </ul>
);

const STRENGTH_BG = {
  "High": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Moderate-High": "bg-teal-50 text-teal-700 border-teal-200",
  "Moderate": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Low": "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Dashboard({ role, onOpenEvidence }) {
  const [exec, setExec] = useState(null);
  const [claims, setClaims] = useState(null);

  useEffect(() => {
    api.get("/executive").then((r) => setExec(r.data));
    api.get("/claims/traceability").then((r) => setClaims(r.data.claims)).catch(() => {});
  }, []);

  const claimForPoint = (label) => claims?.find((c) => c.label.startsWith(`${label}:`));
  const claimForInsight = (id) => claims?.find((c) => c.legacy_id === id);

  if (!exec) return <div className="p-8 text-slate-400 text-sm" data-testid="dashboard-loading">Loading intelligence…</div>;

  return (
    <div className="space-y-5" data-testid="dashboard-view">
      {role && <RoleBriefing role={role} />}

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6" data-testid="exec-summary">
        <p className="font-mono text-[10px] uppercase tracking-widest text-teal-400 mb-3">What does the intelligence tell us? <span className="ml-2 text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 normal-case font-sans font-medium" data-testid="demo-data-tag">Demonstration data</span></p>
        {exec.summary_points ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-5xl" data-testid="exec-summary-points">
            {exec.summary_points.map((p) => (
              <div key={p.label} className="flex items-start gap-3">
                <span className="font-mono text-[9px] uppercase tracking-widest text-teal-400 bg-teal-500/10 border border-teal-500/25 rounded px-1.5 py-0.5 mt-0.5 shrink-0 whitespace-nowrap">{p.label}</span>
                <p className="text-sm text-slate-200 leading-snug">{p.text} <ClaimSources claim={claimForPoint(p.label)} compact /></p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-200 leading-relaxed max-w-5xl">{exec.overall_summary}</p>
        )}
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 pt-4 border-t border-slate-800">
          <div className="flex items-center gap-2" data-testid="exec-confidence">
            <ShieldCheck className="w-4 h-4 text-teal-400" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Assessment confidence</p>
              <p className="text-xs text-white font-semibold">{exec.assessment_confidence.level} · {exec.assessment_confidence.score}/100</p>
              {exec.assessment_confidence.interpretation && <p className="text-[10px] text-teal-200/80 mt-0.5 max-w-xs leading-snug" data-testid="confidence-interpretation">{exec.assessment_confidence.interpretation}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="exec-daterange">
            <CalendarDays className="w-4 h-4 text-teal-400" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Intelligence window · evidence cut-off</p>
              <p className="text-xs text-white font-semibold">{exec.date_range.from} → {exec.date_range.to} · cut-off {exec.date_range.evidence_cutoff}</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="exec-sources">
            <Layers className="w-4 h-4 text-teal-400" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Sources assessed · governed model</p>
              <p className="text-xs text-white font-semibold">
                {exec.sources_assessed.source_documents} source documents · {exec.sources_assessed.source_items} source items · {exec.sources_assessed.hcp_statements} curated HCP statements · {exec.sources_assessed.evidence_records} evidence records
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Documents: {exec.sources_assessed.breakdown.map((b) => `${b.type} ${b.n}`).join(" · ")}
              </p>
            </div>
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mt-2">{exec.assessment_confidence.rationale}</p>
        {exec.eligibility && (
          <p className="text-[10px] text-teal-200/70 mt-1.5" data-testid="exec-eligibility-note">
            <span className="font-mono text-[8.5px] uppercase tracking-widest text-slate-500 mr-1.5">Eligibility gates</span>
            {exec.eligibility.eligible} items eligible for approved outputs · {exec.eligibility.blocked} blocked — {exec.eligibility.note}
          </p>
        )}
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Five Most Important Insights</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="top-insights">
          {exec.top_insights.map((t, idx) => (
            <div key={t.id} data-testid={`exec-insight-${t.id}`} className={`bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm hover:border-teal-400 transition-colors ${idx === 0 ? "lg:col-span-2 xl:col-span-1 border-l-4 border-l-teal-500" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-sm font-bold text-slate-900 leading-snug">
                  <span className="font-mono text-slate-300 mr-1.5">{String(idx + 1).padStart(2, "0")}</span>{t.title}
                </h3>
                <span className="font-mono text-xs font-bold text-teal-700 shrink-0">{t.confidence}<span className="text-slate-400 font-normal">/100</span></span>
              </div>
              <div className="mt-2.5 space-y-1.5">
                <p className="text-[11.5px] text-slate-700 leading-snug"><span className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mr-1.5">Why it matters</span>{t.why_it_matters.split(". ")[0].replace(/\.$/, "")}.</p>
                <p className="text-[11.5px] text-slate-700 leading-snug"><span className="font-mono text-[9px] uppercase tracking-widest text-teal-700 mr-1.5">Recommended action</span>{t.recommended_action.split(";")[0].replace(/\.$/, "")}.</p>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <Badge variant="outline" className={`text-[10px] ${STRENGTH_BG[t.evidence_strength]}`}>Evidence: {t.evidence_strength}</Badge>
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">{t.supporting_sources} sources</Badge>
                {t.geographies.map((g) => <Badge key={g} variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">{g}</Badge>)}
              </div>
              <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-slate-100">
                <span className="flex items-center gap-2">
                  <button
                    data-testid={`exec-evidence-link-${t.id}`}
                    onClick={() => onOpenEvidence?.(t.evidence_ids)}
                    className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
                  >
                    Underlying evidence: {t.evidence_ids.join(", ")} →
                  </button>
                  <ClaimSources claim={claimForInsight(t.id)} />
                </span>
                <span className="text-[10px] text-slate-400">{t.hcp_voices.split("·")[0].trim()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={Zap} title="Top Adoption Accelerators" testid="exec-accelerators"><Li items={exec.accelerators} dot="bg-emerald-500" /></Section>
        <Section icon={AlertTriangle} title="Top Adoption Barriers" testid="exec-barriers"><Li items={exec.barriers} dot="bg-red-500" /></Section>
        <Section icon={CheckCircle2} title="Areas of Strong HCP Consensus" testid="exec-consensus"><Li items={exec.strong_consensus} dot="bg-emerald-500" /></Section>
        <Section icon={GitCompareArrows} title="Disagreement & Uncertainty" testid="exec-disagreement"><Li items={exec.disagreement} dot="bg-amber-500" /></Section>
        <Section icon={FlaskConical} title="Most Important Evidence Gaps" testid="exec-gaps"><Li items={exec.evidence_gaps} dot="bg-indigo-500" /></Section>
        <Section icon={Radar} title="Emerging Clinical Signals" testid="exec-signals"><Li items={exec.emerging_signals} dot="bg-purple-500" /></Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={Crosshair} title="Competitive Threats" testid="exec-threats"><Li items={exec.competitive.threats} dot="bg-red-500" /></Section>
        <Section icon={TrendingUp} title="Competitive Opportunities" testid="exec-opportunities"><Li items={exec.competitive.opportunities} dot="bg-emerald-500" /></Section>
      </div>

      <Section icon={Globe2} title="Differences Across Major Geographies" testid="exec-geo">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {exec.geo_differences.map((g) => (
            <div key={g.region} className="bg-slate-50 border border-slate-200/80 rounded-lg p-3 h-full flex flex-col">
              <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-1.5 flex items-center gap-1.5">
                <span className="w-5 h-5 rounded-md bg-teal-100 border border-teal-200 flex items-center justify-center"><Globe2 className="w-3 h-3 text-teal-700" /></span>{g.region}
              </p>
              <p className="text-xs text-slate-700 leading-snug flex-1">{g.text}</p>
              <p className="font-mono text-[9px] text-slate-400 mt-2">[{g.ids.join(", ")}]</p>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={LineChart} title="Signals to Track Over Time" testid="exec-change">
        <p className="text-[11px] text-slate-500 mb-3">This cross-sectional assessment sets the baseline. Deltas activate when a monthly, quarterly or congress-triggered refresh is enabled.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200">
                {["Indicator", "Baseline (this assessment)", "Direction to watch", "Note"].map((h) => (
                  <th key={h} className="font-mono text-[9px] uppercase tracking-widest text-slate-400 py-2 pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {exec.change_indicators.map((c) => (
                <tr key={c.metric} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 text-xs font-semibold text-slate-800">{c.metric}</td>
                  <td className="py-2 pr-4 text-xs text-slate-600">{c.baseline}</td>
                  <td className="py-2 pr-4 text-xs font-mono text-teal-700">{c.direction}</td>
                  <td className="py-2 text-[11px] text-slate-500">{c.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section icon={Target} title="Priority Strategic Actions" testid="exec-actions">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
          {exec.priority_actions.map((a, i) => (
            <div key={a.action} className="flex items-start gap-2.5 bg-slate-50 border border-slate-200/80 rounded-lg p-3">
              <span className="font-mono text-xs font-bold text-teal-700 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="text-xs text-slate-800 font-medium leading-snug">{a.action}</p>
                <p className="text-[10px] text-slate-500 mt-1">{a.owner} · <span className={a.urgency === "Now" ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"}>{a.urgency}</span>
                  {a.implication_id && <span className="font-mono text-[9px] text-teal-700 ml-1.5" data-testid={`priority-action-ref-${a.implication_id}`}>· {a.implication_id.toUpperCase()} — governed record{a.review_status ? ` (${a.review_status})` : ""}</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={ShieldCheck} title="Methodology and Confidence" testid="exec-methodology">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ul className="space-y-2">
            {(exec.methodology || []).map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-700 leading-snug">
                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-teal-500" />{m}
              </li>
            ))}
          </ul>
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Assessment confidence</p>
            <p className="text-sm font-bold text-slate-900">{exec.assessment_confidence.level} · {exec.assessment_confidence.score}/100</p>
            {exec.assessment_confidence.interpretation && <p className="text-[11px] font-semibold text-teal-800 mt-1">Interpretation: {exec.assessment_confidence.interpretation}</p>}
            <p className="text-[11px] text-slate-600 leading-snug mt-1">{exec.assessment_confidence.rationale}</p>
            <p className="text-[10px] text-slate-500 mt-2">Every confidence rating is a transparent weighted composite of eight named component scores — open Calibration & Scoring to inspect components, weights and rationale. No unexplained scores.</p>
          </div>
        </div>
      </Section>

      <div className="pt-1">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-3" data-testid="geo-section-title">Geographic & Specialty Intelligence</h2>
        <GeoSpecialty filters={{}} />
      </div>
    </div>
  );
}
